import React, { useCallback, useEffect, useId, useState } from 'react'
import { Link, NavLink, useLocation, useParams } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../../shared/error/server-message'
import { keyErrorMessage } from '../../../../../shared/partner/partner-words'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { agoText, dateTimeText, utcText } from '../../../../../shared/date/local-time'
import { formatCount } from '../../../../../shared/sso-workspace/format'
import { getAccess, getKey, listRoles, updateKey } from '../api'
import { noteApiAccess } from '../use-api-access'
import { ROTATE_STAFF_ONLY, useKeyWrites } from '../key-access'
import { useKeyGrantsVisible } from '../grants-access'
import { KeyPermissions } from './api-key-permissions'
import { ACCESS_OFF_BANNER, BOUND_ROLE_NOTE, NEVER_EXPIRES_WARNING, NeverExpiresPill, StatusChip, bindingText, descriptionProblem, isLive, keyStatus, nameProblem, byWhom, prefixText, rolesForKey } from '../key-words'
import { useKeyActions } from './api-key-actions'
import { ApiKeyUsage } from './api-key-usage'
import { ApiKeyRequests } from './api-key-requests'
import '../api-keys.scss'

// One API key (Task 15): its Overview (what it is, who made it, its limit
// and expiry, each secret; Edit renames it, describes it and changes its
// role), its Usage and its Requests, as tabs in ?tab=. Rotate and Revoke
// sit in the header while the key is live.

export const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'usage', label: 'Usage' },
  { key: 'requests', label: 'Requests' }
]

const When = ({ value }) => (value ? <time dateTime={new Date(value).toISOString()} title={utcText(value)}>{dateTimeText(value)}</time> : null)

const Row = ({ label, children }) => (children === null || children === undefined || children === '' ? null : <><dt>{label}</dt><dd>{children}</dd></>)

// A secret version: in use, in its grace (until when), or stopped.
const secretState = secret => {
  if (!secret.live) return 'Stopped'
  if (secret.expires_at) return null
  return 'In use'
}

const Secrets = ({ secrets }) => (
  <FleetSection id="ak-secrets" title="Secrets" lede="A rotation adds a secret; the old one keeps working for its grace, then stops.">
    {!secrets || !secrets.length
      ? <p className="sw-hint m-0">No secrets.</p>
      : <ul className="list-unstyled m-0">
        {secrets.map(secret =>
          <li key={`${secret.prefix}-${secret.created_at}`} className="mb-2">
            <span className="sw-mono me-2">{prefixText(secret.prefix)}</span>
            {secretState(secret)
              ? <span className={secret.live ? 'fw-semibold' : 'sw-hint'}>{secretState(secret)}</span>
              : <span>Works until <When value={secret.expires_at} /></span>}
            <span className="sw-hint small d-block">
              Made <When value={secret.created_at} />
              {' · '}{secret.last_used_at ? <span title={utcText(secret.last_used_at)}>Last used {agoText(secret.last_used_at)}</span> : 'Never used'}
            </span>
          </li>)}
      </ul>}
  </FleetSection>
)

// Rename, describe, change the role: only the fields that changed are sent.
const EditForm = ({ apiKey, onSaved, onCancel }) => {
  const ids = { name: useId(), description: useId(), role: useId() }
  const [name, setName] = useState(apiKey.name || '')
  const [description, setDescription] = useState(apiKey.description || '')
  const [roleID, setRoleID] = useState(apiKey.role ? String(apiKey.role.id) : '')
  const [roles, setRoles] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()

  useEffect(() => {
    let live = true
    listRoles().then(list => { if (live) setRoles(Array.isArray(list) ? list : []) }, err => { if (live) setError(serverMessage(err, "The roles you can give a key couldn't be loaded."), err) })
    return () => { live = false }
  }, [setError])

  // A bound key is offered only its binding's roles (core refuses any other:
  // 403 api_key.role_not_for_binding). The key's own role is always shown, so
  // the select is never blank; when it isn't one this person could give (core
  // hides the roles that carry AI tools telemetry from anyone but staff, and a
  // custom key's own role is never offered) it is the selected option but not
  // a choice: disabled.
  const offered = rolesForKey(apiKey, roles)
  const ungivable = !!apiKey.role && !offered.some(r => r.id === apiKey.role.id)
  const options = ungivable ? [apiKey.role, ...offered] : offered
  const boundNote = apiKey.binding ? BOUND_ROLE_NOTE[apiKey.binding.kind] : null

  const save = async e => {
    e.preventDefault()
    if (busy) return
    const problem = nameProblem(name) || descriptionProblem(description)
    if (problem) return setError(problem)
    const body = {}
    if (name.trim() !== apiKey.name) body.name = name.trim()
    if (description.trim() !== (apiKey.description || '')) body.description = description.trim()
    if (roleID && (!apiKey.role || Number(roleID) !== apiKey.role.id)) body.role_id = Number(roleID)
    if (!Object.keys(body).length) return onCancel()
    setBusy(true)
    setError(null)
    try {
      const saved = await updateKey(apiKey.id, body)
      setBusy(false)
      onSaved(saved)
    } catch (err) {
      setBusy(false)
      setError(keyErrorMessage(err, "The key couldn't be saved."), err)
    }
  }

  return (
    <form onSubmit={save} noValidate>
      <div className="ak-field">
        <label className="sw-label" htmlFor={ids.name}>Name</label>
        <input id={ids.name} className="form-control" value={name} maxLength={200} onChange={e => setName(e.target.value)} />
      </div>
      <div className="ak-field">
        <label className="sw-label" htmlFor={ids.description}>Description</label>
        <textarea id={ids.description} className="form-control" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
      </div>
      <div className="ak-field">
        <label className="sw-label" htmlFor={ids.role}>Role</label>
        <select id={ids.role} className="form-select" value={roleID} onChange={e => setRoleID(e.target.value)}>
          {options.map(role => <option key={role.id} value={String(role.id)} disabled={ungivable && role.id === apiKey.role.id}>{role.name}</option>)}
        </select>
        <p className="sw-hint small mt-1 mb-0">{boundNote ? `${boundNote} ` : ''}{apiKey.custom ? "Choosing a role replaces this key's custom permissions. " : ''}A key can only be given a role whose permissions you hold yourself.</p>
      </div>
      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mb-2" />}
      <div className="d-flex gap-2">
        <button type="submit" className="btn sw-btn-primary" disabled={busy}>Save</button>
        <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

const Overview = ({ apiKey, canEdit, grantsVisible, onSaved }) => {
  const [editing, setEditing] = useState(false)
  const status = keyStatus(apiKey)
  const revoked = status === 'revoked'

  return (
    <>
      <FleetSection id="ak-about" title="About this key"
        actions={canEdit && !editing ? <button type="button" className="btn sw-btn btn-sm" onClick={() => setEditing(true)}>Edit</button> : null}>
        {editing
          ? <EditForm apiKey={apiKey} onCancel={() => setEditing(false)} onSaved={saved => { setEditing(false); onSaved(saved) }} />
          : <dl className="sw-dl">
            <Row label="Key"><span className="sw-mono">{prefixText(apiKey.prefix)}</span></Row>
            <Row label="Role">{apiKey.role ? apiKey.role.name : ''}</Row>
            <Row label="System key">
              {apiKey.system_key
                ? <><span className="sw-mono">{apiKey.system_key}</span><span className="sw-hint small d-block">Set when the key was made. It can't be changed.</span></>
                : <span className="sw-hint">None. A key's system key is set only when it's made.</span>}
            </Row>
            <Row label="Binding">{bindingText(apiKey)}</Row>
            <Row label="Status"><StatusChip status={status} /></Row>
            <Row label="Description">{apiKey.description}</Row>
            <Row label="Rate limit">{apiKey.rate_limit_per_minute ? `${formatCount(apiKey.rate_limit_per_minute)} requests a minute` : ''}</Row>
            <Row label="Created"><><When value={apiKey.created_at} />{` by ${byWhom(apiKey.created_by, apiKey.created_by_support)}`}</></Row>
            <Row label="Expires">
              {apiKey.expires_at
                ? <When value={apiKey.expires_at} />
                : revoked
                  ? <span className="sw-hint">Revoked</span>
                  : <><NeverExpiresPill /><span className="sw-hint small d-block">{NEVER_EXPIRES_WARNING}</span></>}
            </Row>
            <Row label="Last used">{apiKey.last_used_at ? <span title={utcText(apiKey.last_used_at)}>{agoText(apiKey.last_used_at)}</span> : 'Never used'}</Row>
            {revoked && <Row label="Revoked"><><When value={apiKey.revoked_at} />{` by ${byWhom(apiKey.revoked_by, apiKey.revoked_by_support)}`}</></Row>}
            {revoked && <Row label="Reason">{apiKey.revoke_reason}</Row>}
          </dl>}
      </FleetSection>
      {grantsVisible && <KeyPermissions apiKey={apiKey} canEdit={canEdit} onSaved={onSaved} />}
      <Secrets secrets={apiKey.secrets} />
    </>
  )
}

export const ApiKeyDetail = () => {
  const { keyID } = useParams()
  const { pathname, search } = useLocation()
  const writes = useKeyWrites()
  const grantsVisible = useKeyGrantsVisible()
  const [apiKey, setApiKey] = useState(undefined)
  const [error, failure, setError] = useErrorLine()

  const load = useCallback(() => getKey(keyID).then(
    found => { setError(null); setApiKey(found || null) },
    err => { setApiKey(null); setError(serverMessage(err, "This API key couldn't be loaded."), err) }
  ), [keyID, setError])
  useEffect(() => { load() }, [load])

  // The organization's API access, read afresh when the page opens (staff
  // may have switched it) and handed to the sidebar's entry too: while it is
  // off this key is refused.
  const [access, setAccess] = useState(null)
  const loadAccess = useCallback(() => getAccess().then(a => { setAccess(a || null); noteApiAccess(a || null) }, () => setAccess(null)), [])
  useEffect(() => { loadAccess() }, [loadAccess])

  const changed = useCallback(() => {
    load()
    loadAccess()
  }, [load, loadAccess])
  const actions = useKeyActions({ onChanged: changed })

  const asked = new URLSearchParams(search).get('tab')
  const tab = pathname.includes('/requests/') ? 'requests' : (TABS.find(t => t.key === asked) || TABS[0]).key
  const base = `/org/api-keys/${keyID}`

  const live = !!apiKey && isLive(apiKey)
  const canRotate = live && writes.canRotate(apiKey)
  const headerActions = live && (canRotate || writes.canDelete)
    ? <>
      {canRotate && <button type="button" className="btn sw-btn" onClick={() => actions.askRotate(apiKey)}>Rotate</button>}
      {writes.canDelete && <button type="button" className="btn sw-btn-danger" onClick={() => actions.askRevoke(apiKey)}>Revoke</button>}
    </>
    : null

  return (
    <FleetPage
      title={apiKey ? apiKey.name : 'API key'}
      purpose={apiKey ? <><StatusChip status={keyStatus(apiKey)} />{apiKey.role ? ` ${apiKey.role.name}` : ''}</> : null}
      actions={headerActions}
      testId="api-key-page">
      <p className="mb-2"><Link to="/org/api-keys">← All API keys</Link></p>
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {apiKey === undefined && !error && <p className="sw-hint" role="status">Loading…</p>}
      {apiKey &&
        <>
          {access && access.enabled === false && <Notice tone="bad" role="alert">{ACCESS_OFF_BANNER}</Notice>}
          {writes.readOnlyReason && <Notice tone="warn">{writes.readOnlyReason}</Notice>}
          {live && writes.rotateStaffOnly(apiKey) && <Notice>{ROTATE_STAFF_ONLY}</Notice>}
          <nav className="sw-tabs" aria-label="API key">
            {TABS.map(t =>
              <NavLink key={t.key} to={{ pathname: base, search: t.key === 'overview' ? '' : `?tab=${t.key}` }} isActive={() => t.key === tab}
                activeClassName="active" aria-current={t.key === tab ? 'page' : undefined}>{t.label}</NavLink>)}
          </nav>
          {tab === 'overview' && <Overview apiKey={apiKey} canEdit={writes.canUpdate && live} grantsVisible={grantsVisible} onSaved={saved => setApiKey(saved || apiKey)} />}
          {tab === 'usage' && <ApiKeyUsage keyID={apiKey.id} />}
          {tab === 'requests' && <ApiKeyRequests keyID={apiKey.id} />}
        </>}
      {actions.dialogs}
    </FleetPage>
  )
}
